import assert from '@/sui/tools/assert'

import results from '@/sui/data/results'
import regions from '@/sui/tools/regions'
import patient from '@/sui/data/sheets/edit/patient'
import bact from '@/sui/data/sheets/edit/bact'
import pneu from '@/sui/data/sheets/edit/pneu'
import intub from '@/sui/data/sheets/edit/intub'
import catheter from '@/sui/data/sheets/edit/catheter'
import multi from '@/sui/data/sheets/edit/multi'
import micro from '@/sui/data/microOrgs'
import reaInfADI from '@/sui/data/sheets/edit/cfg/reaInfADI'

import clone from 'clone'

function valuesFromField (ctx, forceCodes) {
  const values = ctx.values
  if (!forceCodes) {
    return values
  }

  const result = []
  for (let i = 0; i !== values.length; ++i) {
    const v = values[i]
    const s = v.value.split('.')
    if (s.length === 2) {
      result.push({ code: v.code, value: `${v.code}.${s[1]}` })
    } else {
      result.push(v)
    }
  }
  return result
}

export function fromField (key, field, neutral, forceCodes, context) {
  assert(
    field != null && field.config != null,
    `fromField: field is null for key ${key}`
  )

  const ctx = field.buildContext(context != null ? context() : {}, {})
  return {
    f: key,
    t: ctx.title,
    v: valuesFromField(ctx, forceCodes),
    n: neutral,
    minimizeDecimalScale: field.config.digits != null
  }
}

export function fromFieldWithDetail (key, field, neutral, forceCodes, context) {
  const renamedField = clone(field)
  renamedField.config.title = `${field.config.title} + Autre`
  return fromField(key, renamedField, neutral, forceCodes, context)
}

export function repFromNumericFields (key, field) {
  assert.equals(
    field.type, 'numeric', `repFromNumericFields: Invalid type for field ${key}`
  )
  const values = []
  const count = Math.pow(10, field.config.digits)
  for (let i = 0; i !== count; ++i) {
    values.push(
      {
        code: i,
        value: `${i} ${field.config.unit}`
      }
    )
  }
  return {
    f: key,
    t: `${field.config.title} (Répartition)`,
    v: values
  }
}

export const yesno = [
  { code: 1, value: 'Oui' },
  { code: 2, value: 'Non' }
]

const estbValues = []
for (let t in results.establishments) {
  estbValues.push(
    {
      code: t,
      value: results.establishments[t].title
    }
  )
}
estbValues.push(
  {
    code: 'none',
    value: '(Non renseigné)'
  }
)

const regionValues = []
for (let i = 0; i !== regions.filters.length; ++i) {
  regionValues.push(
    {
      code: i,
      value: regions.filters[i].name
    }
  )
}

export const discValues = []
const keys = {}
const years = ['2023', '2024']
for (let i = 0; i !== years.length; ++i) {
  const lv = results.specialities(years[i])
  for (let k in lv) {
    const u = lv[k]
    if (keys[k] == null) {
      discValues.push(
        {
          code: u.index,
          value: u.code
        }
      )
      keys[k] = true
    }
  }
}

export function locDistribution (prefix, title, opts = {}) {
  const result = opts.estb ? [
    {
      f: `${prefix}etbtype`,
      t: `Type d'établissement`,
      v: estbValues
    },
    {
      f: `${prefix}etbregion`,
      t: 'Région',
      v: regionValues
    }
  ] : []

  const utv = opts.year >= 2024 ? 'v2' : 'v1'
  result.push(
    {
      f: `${prefix}unitType`,
      t: 'Spécialité',
      v: patient.unitType[utv].rw.config.values
    },
    {
      f: `${prefix}discipline`,
      t: `Discipline`,
      v: discValues
    }
  )
  return [
    {
      key: `${prefix}loc`,
      title: `Provenance des ${title}`,
      open: opts.open,
      list: result
    }
  ]
}

export function igsGroups (prefix, complete) {
  if (!complete) {
    return []
  }

  return [
    {
      f: `${prefix}igsPneu`,
      t: `Répartition des IGSII (REZO Pneu)`,
      v: [
        { code: 'igsPneu1', value: 'entre 0 et 35' },
        { code: 'igsPneu2', value: 'entre 36 et 47' },
        { code: 'igsPneu3', value: 'entre 48 et 61' },
        { code: 'igsPneu4', value: 'supérieur ou égal à 62' }
      ]
    },
    {
      f: `${prefix}igsBact`,
      t: `Répartition des IGSII (REZO Bact)`,
      v: [
        { code: 'igsBact1', value: 'entre 0 et 34' },
        { code: 'igsBact2', value: 'entre 35 et 46' },
        { code: 'igsBact3', value: 'entre 47 et 59' },
        { code: 'igsBact4', value: 'supérieur ou égal à 60' }
      ]
    }
  ]
}

export function patientDistribution (prefix, title, complete) {
  return {
    key: `${prefix}pat`,
    title: `Caractéristiques des patients présentant une ${title}`,
    list: [
      { f: `${prefix}agey`, t: 'Âge (en années)', minimizeDecimalScale: true },
      fromField(`${prefix}sex`, patient.sex, 9),
      fromField(`${prefix}immuno`, patient.immuno, 9),
      fromField(`${prefix}cancer`, patient.cancer, 9),
      fromField(`${prefix}trauma`, patient.trauma, 9),
      fromField(`${prefix}diagnosis`, patient.diagnosis, 9),
      fromField(`${prefix}antibio`, patient.antibio, 9),
      fromField(`${prefix}portageBmrBhre`, patient.portageBmrBhre, 9),
      fromField(`${prefix}dead`, patient.dead, 9),
      fromField(`${prefix}covidStatus`, multi.covidStatus.std, 9),
      fromField(`${prefix}ecmo`, intub.ecmo),
      fromField(`${prefix}intubated`, intub.intubated.std),
      fromField(`${prefix}reintubated`, intub.reintubated.std),
      { f: `${prefix}itbLapse`, t: `Durée d'intubation`, minimizeDecimalScale: true },
      { f: `${prefix}origin`, t: 'Adultes et enfants', h: true },
      fromField(`${prefix}origin`, patient.patientOrigin.v3, 9),
      fromField(`${prefix}bmi`, patient.bmi),
      { f: `${prefix}aged`, t: 'Nouveaux-nés', h: true, minimizeDecimalScale: true },
      { f: `${prefix}aged`, t: 'Âge (en jours)', minimizeDecimalScale: true },
      fromField(`${prefix}neoOrigin`, patient.neoOrigin, 9),
      fromField(`${prefix}birthWeight`, patient.birthWeight),
      fromField(`${prefix}gestationalAge`, patient.gestationalAge),
      fromField(`${prefix}cribII`, patient.cribII.std),
      { f: `${prefix}pimII`, t: 'Enfants', h: true },
      fromField(`${prefix}pimII`, patient.pimII.std),
      { f: `${prefix}igsII`, t: 'Adultes', h: true },
      fromField(`${prefix}igsII`, patient.igsII),
      ...igsGroups(prefix, complete)
    ]
  }
}

export function bactDistribution (p, title, opt = {}) {
  const list = [
    {
      f: `${p}entranceCategory`,
      t: `Distribution des catégories de porte d'entrée`,
      v: [
        { code: 1, value: 'Cathéters centraux' },
        { code: 2, value: 'MID line' },
        { code: 3, value: 'CVP court' },
        { code: 4, value: 'Cathéter de dialyse' },
        { code: 5, value: 'Infection du site opératoire' },
        { code: 6, value: 'Pleuro-pulmonaire' },
        { code: 7, value: 'Urinaire' },
        { code: 8, value: 'Digestive / Abdominale' },
        { code: 10, value: 'Autres' },
        { code: 9, value: 'Non retrouvée' }
      ]
    },
    {
      f: `${p}entrance`,
      t: `Distribution des portes d'entrée`,
      v: entranceWithMIDandCVP
    },
    {
      f: `${p}mid`,
      t: `Distribution des portes d'entrée MID line`,
      v: bact.entrance.v3.all.config.values
    },
    {
      f: `${p}cvp`,
      t: `Distribution des portes d'entrée CVP court`,
      v: bact.entrance.v3.all.config.values
    },
    {
      f: `${p}bactOrigin`,
      t: 'Origine',
      v: bact.bactOrigin.v2.config.values
    },
    ...entryEpisodLapse(p)
  ]

  if (opt.included) {
    list.push(includedInfection(''))
  }

  if (opt.covid) {
    list.push(fromField(`${p}covidStatus`, multi.covidStatus.std, 9))
  }

  list.push(
    {
      f: `${p}episodImmuno`,
      t: `Pour les bactériémies acquises en services d'hématologie ou de cancérologie`,
      h: true
    },
    fromField(`${p}episodImmuno`, bact.episodImmuno, 9),
    {
      f: `${p}insertionSite`,
      t: 'Pour les bactériémies associées à un dispositif intra-vasculaire (C1-14 et D1)',
      h: true
    },
    fromField(`${p}insertionSite`, bact.insertionSite.full, 9),
    {
      f: `${p}divDiag`,
      t: 'Critère diagnostique',
      v: bact.divDiag.v2.config.values,
      n: 9
    },
    {
      f: `${p}neoDivDiag`,
      t: 'Critère diagnostique (nouveau-né)',
      v: bact.divDiag.v1.neo.config.values,
      n: 9
    },
    fromField(`${p}lipids`, bact.lipids.v2, 9),
    fromField(`${p}removed`, catheter.removed, 9),
    {
      f: `${p}ktLapse`,
      t: `Durée du cathétérisme`,
      minimizeDecimalScale: true
    },
    {
      f: `${p}ktEpisodLapse`,
      t: `Délai entre la date de pose et la date de l'épisode`,
      minimizeDecimalScale: true
    },
    {
      f: `${p}ktEpisodLapse7d`,
      t: `Répartition du délai entre la date de pose et la date de
l'épisode`,
      v: [
        { code: 0, value: '<= 7 jours' },
        { code: 1, value: '> 7 jours' }
      ]
    },
    fromField(`${p}tunneled`, bact.tunneled),
    {
      f: `${p}previousManip`,
      t: 'Pour les bactériémies associées à une CCI, un PICC ou un MIDline avec prolongateur (C2, C4 et C8)',
      h: true
    },
    fromField(`${p}previousManip`, bact.previousManip, 9),
    fromField(`${p}previousManipType`, bact.previousManipType.v2, 9),
    {
      f: `${p}manipEpisodLapse`,
      t: `Délai entre la date de l'épisode et la date de manipulation`,
      minimizeDecimalScale: true
    },
    {
      f: `${p}clinicalSigns`,
      t: 'Pour les bactériémes avec un critère diagnostique à 5',
      h: true
    },
    fromField(`${p}clinicalSigns`, bact.clinicalSigns, 9),
    fromField(`${p}bioSigns`, bact.bioSigns, 9),
    fromField(`${p}adaptedAntibio`, bact.adaptedAntibio, 9),
    fromField(`${p}twoHemocs`, bact.twoHemocs, 9),
    {
      f: `${p}urinaryPresent`,
      t: `Pour les bactériémies associées à une porte d'entrée urinaire (A4)`,
      h: true
    },
    {
      f: `${p}urinaryPresent`,
      t: `Sondage urinaire dans les 7 jours précédant l'épisode infectieux`,
      v: reaInfADI.urinary.config.values
    },
    {
      f: `${p}urinary`,
      t: `Détail du sondage urinaire`,
      v: bact.urinary.v1.std.config.values
    },
    {
      f: `${p}urinary_v2`,
      t: `Détail du sondage urinaire`,
      v: bact.urinary.v2.std.config.values
    },
    fromField('urinaryDiag', bact.urinaryDiag.v2, 9),
    {
      f: `${p}a3intubated`,
      t: `Pour les bactériémies associées à une porte d'entrée
pleuro-pulmonaire (A3)`,
      h: true
    },
    fromField(`${p}a3intubated`, intub.intubated.std),
    fromField(`${p}a3reintubated`, intub.reintubated.std),
    { f: `${p}a3intubLapse`, t: `Durée d'intubation` },
    {
      f: `${p}catheter`,
      t: `Pour les bactériémies sans porte d'entrée retrouvée (A13)`,
      h: true
    },
    fromField(`${p}catheter`, bact.enfCatheter.v2, 9),
    fromField(`${p}catheterType`, bact.enfCatheterType.v3.all, 9),
    {
      f: `${p}a13KtEpisodLapse`,
      t: `Délai entre la date de pose et la date de l'épisode`,
      minimizeDecimalScale: true
    },
    {
      f: `${p}a13KtEpisodLapse7d`,
      t: `Répartition du délai entre la date de pose et la date de
l'épisode`,
      v: [
        { code: 0, value: '<= 7 jours' },
        { code: 1, value: '> 7 jours' }
      ]
    },
    fromField(`${p}enfTunneled`, bact.enfTunneled),
    {
      f: `${p}a13previousManip`,
      t: 'Pour les bactériémies A13 associées à une CCI, un PICC ou un MIDline avec prolongateur (C2, C4 et C8)',
      h: true
    },
    fromField(`${p}a13previousManip`, bact.previousManip),
    fromField(`${p}a13previousManipType`, bact.previousManipType.v2),
    {
      f: `${p}a13manipEpisodLapse`,
      t: `Délai entre la date de l'épisode et la date de manipulation`,
      minimizeDecimalScale: true
    }
  )

  return {
    key: 'bact',
    title: `Caractéristiques des ${title}`,
    list: list
  }
}

export function pneuDistribution (covid, included) {
  const list = [
    {
      f: 'p_entrance',
      t: `Type de pneumopathie`,
      v: [
        { code: 'P1', value: `Associées à l'intubation` },
        { code: 'P2', value: `Non associées à l'intubation` }
      ]
    },
    {
      f: 'p_pneuOrigin',
      t: 'Origine',
      v: pneu.origin.std.config.values
    },
    fromField('p_pneuDiag', pneu.pneuDiag.std),
    ...entryEpisodLapse('p_')
  ]

  if (included) {
    list.push(includedInfection('p_'))
  }

  if (covid) {
    list.push(fromField(`p_covidStatus`, multi.covidStatus.std, 9))
  }
  return {
    key: 'pneu',
    title: `Caractéristiques des pneumopathies`,
    list: list
  }
}

export function moDistribution (prefix, title, year) {
  const srnc = [
    { code: 0, value: 'S ou SFP - Sensible' },
    { code: 1, value: 'I/R ou R - Résistant' },
    { code: 9, value: 'NC - Non connu' }
  ]

  return {
    key: `${prefix}mo`,
    title: `Caractéristiques des micro-organismes associés aux ${title}`,
    list: [
      {
        f: `${prefix}moCategory`,
        t: `Distribution des catégories de micro-organismes`,
        v: [
          { code: 1, value: 'Staphylococcus aureus' },
          { code: 2, value: 'Autres staphylocoques' },
          { code: 3, value: 'Stréptocoques' },
          { code: 4, value: 'Entérocoques' },
          { code: 5, value: 'Entérobactérales' },
          { code: 6, value: 'Acinetobacter' },
          { code: 7, value: 'Pseudomonas aeruginosa' },
          { code: 8, value: 'Candida, Levures et Filamenteux' },
          { code: 9, value: 'Autres micro-organismes' }
        ]
      },
      {
        f: `${prefix}microOrganisms`,
        t: 'Micro-organismes',
        v: micro.completeList.v2,
        n: 104
      },
      {
        f: `${prefix}sarm`,
        h: true,
        t: 'Staphylococcus aureus'
      },
      {
        f: `${prefix}sarm`,
        t: 'Résistance à la méticilline',
        n: 9,
        v: srnc
      },
      {
        f: `${prefix}ebc3g`,
        h: true,
        t: 'Entérobactérales'
      },
      {
        f: `${prefix}ebc3g`,
        t: 'Résistance aux céphalosporines de 3ème génération (C3G)',
        n: 9,
        v: srnc
      },
      {
        f: `${prefix}eblse`,
        t: 'EBLSE (pour les entérobactérales intermédiaires, sensibles à forte posologie ou résistantes aux C3G)',
        n: 2,
        v: micro.blseList
      },
      {
        f: `${prefix}ebcar`,
        t: 'Résistance aux carbapénèmes',
        n: 9,
        v: srnc
      },
      {
        f: `${prefix}epc`,
        t: 'EPC (pour les entérobactérales intermédiaires, sensibles à forte posologie ou résistantes aux carbapénèmes)',
        n: 2,
        v: micro.epcList
      },
      {
        f: `${prefix}epcType`,
        t: `Type d'EPC (pour les entérobactérales intermédiaires, sensibles à forte posologie ou résistantes aux carbapénèmes)`,
        n: 9,
        v: micro.epcTypeList
      },
      {
        f: `${prefix}epcType2`,
        t: `Second type d'EPC (pour les entérobactérales intermédiaires, sensibles à forte posologie ou résistantes aux carbapénèmes)`,
        n: 9,
        v: micro.epcTypeList
      },
      {
        t: 'Pseudomonas aeruginosa',
        h: true,
        f: `${prefix}piocar`
      },
      {
        f: `${prefix}piocar`,
        t: 'Résistance aux carbapénèmes',
        n: 9,
        v: srnc
      },
      {
        f: `${prefix}pioppc`,
        t: 'PPC (pour les PSE AER intermédiaires, sensibles à forte posologie ou résistants aux carbapénèmes)',
        n: 2,
        v: micro.ppcList
      },
      {
        f: `${prefix}pioppcType`,
        t: 'Type de PPC (pour les PSE AER intermédiaires, sensibles à forte posologie ou résistants aux carbapénèmes)',
        n: 9,
        v: micro.ppcTypeList
      },
      {
        f: `${prefix}pioppcType2`,
        t: 'Second type de PPC (pour les PSE AER intermédiaires, sensibles à forte posologie ou résistants aux carbapénèmes)',
        n: 9,
        v: micro.ppcTypeList
      },
      {
        f: `${prefix}acicar`,
        t: 'Acinetobacter baumanii',
        h: true
      },
      {
        f: `${prefix}acicar`,
        t: 'Résistance aux carbapénèmes',
        n: 9,
        v: srnc
      },
      {
        f: `${prefix}canflu`,
        t: 'Candida',
        h: true
      },
      {
        f: `${prefix}canflu`,
        t: 'Résistance au fluconazole',
        n: 2,
        v: micro.fluList
      },
      {
        t: 'Enterococcus faecalis',
        h: true,
        f: `${prefix}encfaegly`
      },
      {
        f: `${prefix}encfaegly`,
        t: 'Résistance aux glycopeptides (Enterococcus faecalis)',
        n: 9,
        v: srnc
      },
      {
        t: 'Enterococcus faecium',
        h: true,
        f: `${prefix}encfaigly`
      },
      {
        f: `${prefix}encfaigly`,
        t: 'Résistance aux glycopeptides (Enterococcus faecium)',
        n: 9,
        v: srnc
      }
    ]
  }
}

function includedInfection (p) {
  return {
    f: `${p}included`,
    t: 'Acquise dans le service (inclue dans le calcul des incidences)',
    v: [
      { code: 1, value: `Oui, acquise dans l'établissement` },
      { code: 2, value: 'Oui, origine inconnue et délai épisode - entrée >= 48h' },
      { code: 3, value: 'Non, acquise hors établissement' },
      { code: 4, value: 'Non, origine inconnue et délai épisode - entrée < 48h' }
    ]
  }
}

const entranceWithMIDandCVP = (function () {
  const result = []
  const list = bact.entrance.v3.all.config.values
  const count = list.length
  for (let i = 0; i !== count; ++i) {
    const v = list[i]
    if (v.code === 'C8') {
      result.push({ code: 'MID', value: 'C8+C9+C12. MID line' })
    } else if (v.code === 'C10') {
      result.push({ code: 'CVP court', value: 'C10+C11+C13. CVP court' })
    } else if (v.code !== 'C9' &&
               v.code !== 'C12' &&
               v.code !== 'C11' &&
               v.code !== 'C13') {
      result.push(v)
    }
  }
  return result
})()

function entryEpisodLapse (prefix) {
  return [
    {
      f: `${prefix}entryEpisodLapse`,
      t: `Délai entre la date de l'épisode et la date d'entrée`,
      minimizeDecimalScale: true
    },
    {
      f: `${prefix}entryEpisodLapse48h`,
      t: `Répartition du délai entre la date de l'épisode et la date d'entrée`,
      v: [
        { code: 0, value: '<= 2 jours' },
        { code: 1, value: '> 2 jours' }
      ]
    }
  ]
}
